import { zodResolver } from '@hookform/resolvers/zod'
import { type CreateProfessionInput, CreateProfessionInputSchema } from '@idc-forms/schemas'
import { Controller, useForm } from 'react-hook-form'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { Switch } from '@/shared/ui/switch'

type ProfessionFormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreateProfessionInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: CreateProfessionInput) => void
  onCancel: () => void
}

/**
 * Formulario de profesión (crear/editar) para el side panel. RHF + zodResolver
 * con el MISMO schema (@idc-forms/schemas) que valida el backend. La fila
 * especial "Otro" nunca llega acá: el backend la administra.
 */
export function ProfessionForm({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: ProfessionFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateProfessionInput>({
    resolver: zodResolver(CreateProfessionInputSchema),
    defaultValues: {
      active: true,
      ...defaultValues
    }
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldGroup className="gap-4">
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.name)}>
              <FieldLabel htmlFor="profession-name">Nombre</FieldLabel>
              <Input
                id="profession-name"
                value={field.value ?? ''}
                onChange={field.onChange}
                autoComplete="off"
              />
              <FieldDescription>
                El nombre debe ser único en el catálogo (sin distinguir mayúsculas).
              </FieldDescription>
              <FieldError errors={[errors.name]} />
            </Field>
          )}
        />

        <Controller
          control={control}
          name="active"
          render={({ field }) => (
            <Field orientation="horizontal">
              <FieldLabel htmlFor="profession-active">Activa</FieldLabel>
              <Switch
                id="profession-active"
                checked={field.value ?? true}
                onCheckedChange={field.onChange}
              />
            </Field>
          )}
        />
      </FieldGroup>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
